<template>
  <v-form v-model="isValid">
    <v-expansion-panels v-if="product.teamLevel" v-model="expandedVariants">
      <v-expansion-panel v-for="(variant, vIndex) in product.variants" :key="vIndex">
        <v-expansion-panel-title>Variant {{ vIndex + 1 }}</v-expansion-panel-title>
        <v-expansion-panel-text>
          "Variant Description"
          <Editor
            v-model="variant.description"
            :init="{
              base_url: '/tinymce',
              suffix: '.min',
              plugins: 'lists link image table code',
              toolbar:
                'undo redo | blocks | bold italic | alignleft aligncenter alignright | bullist numlist | link image table | code',
              skin: 'oxide',
              content_css: 'default',
            }"
            licenseKey="gpl"></Editor>
          <v-text-field
            v-model="variant.price"
            label="Price"
            type="number"
            prefix="$"
            :rules="[rules.required, rules.numeric]"></v-text-field>
          <v-text-field
            v-model="variant.fieldInputStartDate"
            :rules="[rules.required]"
            label="Start Date"
            type="date"></v-text-field>
          <v-text-field
            v-model="variant.fieldInputEndDate"
            :rules="[rules.required]"
            label="End Date"
            type="date"></v-text-field>
          <!-- Product Additions within Variant -->
          <v-expansion-panels v-model="expandedAdditions[vIndex]">
            <v-expansion-panel v-for="(addition, aIndex) in variant.productAdditions" :key="aIndex">
              <v-expansion-panel-title>Addition {{ aIndex + 1 }}</v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-text-field
                  v-model="addition.name"
                  label="Addition Name"
                  :rules="[rules.required]"></v-text-field>
                <v-text-field
                  v-model="addition.description"
                  label="Addition Description"></v-text-field>
                <v-btn color="red" @click="removeAddition(vIndex, aIndex)">Remove Addition</v-btn>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-btn color="red" @click="removeVariant(vIndex)">Remove Variant</v-btn>

          <v-btn color="primary" @click="addAddition(vIndex)">Add Addition</v-btn>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-btn color="primary" @click="addVariant">Add Variant</v-btn>
    <v-btn color="success" :disabled="!isValid || !hasOneVariant" @click="submitProduct"
      >Submit Product</v-btn
    >
  </v-form>
</template>

<script setup lang="ts">
import { IProductDTO, ProductAdditionDTO, ProductDTO, ProductVariantDTO } from '@/dtos';
import { computed, ref, toRef } from 'vue';
import { rules } from '@/helpers';
import Editor from '@tinymce/tinymce-vue';
import { useProductStore } from '@/stores/modules/admin';
import '@/components/tinymce/tinymce';

interface Props {
  yearId: string;
  product: IProductDTO;
  type: string;
}
const props = defineProps<Props>();
const emit = defineEmits(['productCreateSelect', 'productEditSelect']);

const yearId = toRef(props, 'yearId', '');
const product = toRef(props, 'product', new ProductDTO());
const productStore = useProductStore();

const type = toRef(props, 'type', '');
const isValid = ref<boolean>(false);
const hasOneVariant = computed((): boolean => {
  return product.value.variants.length >= 1 ? true : false;
});

const expandedVariants = ref<number>(0);
const expandedAdditions = ref<boolean[][]>(
  product.value.variants.map(() => new Array(0).fill(false)),
);

const addAddition = (variantIndex: number) => {
  if (!expandedAdditions.value[variantIndex]) {
    expandedAdditions.value[variantIndex] = [];
  }

  product.value.variants[variantIndex].productAdditions.push(new ProductAdditionDTO());
  expandedAdditions.value[variantIndex].push(false);
};

const removeAddition = (variantIndex: number, additionIndex: number) => {
  if (expandedAdditions.value[variantIndex]) {
    expandedAdditions.value[variantIndex].splice(additionIndex, 1);
  }

  product.value.variants[variantIndex].productAdditions.splice(additionIndex, 1);
};

const addVariant = () => {
  product.value.variants.push(new ProductVariantDTO());
  expandedVariants.value = product.value.variants.length - 1;
};

// Remove Variant
const removeVariant = (variantIndex: number) => {
  product.value.variants.splice(variantIndex, 1);
  expandedVariants.value = 0;
};

const submitProduct = async () => {
  if (isValid.value) {
    if (type.value == 'create') {
      await productStore.create.mutateAsync({ product: product.value, yearId: yearId.value });
      emit('productCreateSelect');
    }
    if (type.value == 'edit') {
      await productStore.update.mutateAsync({ product: product.value, yearId: yearId.value });
      emit('productEditSelect');
    }
  }
};
</script>
